import { SelectionChangedEvent } from "ag-grid-community";
import { EMPTY_COLLECTION_DTL_SEARCH_CRITERIA } from "domain/entity/Collection/CollectionDetailSearchCriteria";
import _ from "lodash";
import { CollectionConstant } from "presentation/constant/Collection/CollectionConstant";
import { INITIAL_COLLECTION_DTL_COL_DEF, INITIAL_COLLECTION_DTL_COL_DEF_OTHER, transferRowData } from "presentation/constant/Collection/CollectionDtlColumnDefinition";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useCollectionVM } from "presentation/hook/Collection/useCollectionVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCollectionTracked } from "presentation/store/Collection/CollectionProvider";
import { ConfirmModal } from "presentation/view/components/ConfirmModal";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HPHButton, IconButton, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";
import CollectionHeaderBar from "./CollectionHeaderBar";
import NbisTable2 from "presentation/view/components/TableWrapper/NbisTable2";
import { CollectionDetailEntity } from "domain/entity/Collection/CollectionDetailEntity";

const CollectionDtlTablePanel: React.FC = () => {

    const SCREEN_CONSTANT = CollectionConstant;
    const [collectionState] = useCollectionTracked();
    const collectionVM = useCollectionVM();
    const { collectionHdrList, collectionDtlList, isShowEditPanel, selectedDtlRows, selectedRows, totalInvAmt, searchCriteria, payeeNameList } = collectionState;
    const gridRef: any = useRef(null);
    const messageBarVM = useMessageBarVM();
    const [isShowClose, setIsShowClose] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;

    const payeeNameListRef = useRef(payeeNameList);

    const handleAdd = useCallback(() => {
        collectionVM.onShowAddPanel(true).then(data => {
            if (data) {
                messageBarVM.showError(data);
                return;
            }
        });
    }, [collectionVM, messageBarVM])


    const handleDelete = useCallback(async () => {
        if (_.isEmpty(selectedDtlRows)) {
            messageBarVM.showError("Please select a record.");
            return;
        }
        setIsLoading(true);
        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        const ids = selectedDtlRows.map(ety => ety.id);
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            delDtlIds: ids,
            allRows: collectionHdrList
        }
        const hdrIds = selectedRows.map(ety => ety.id);
        await collectionVM.onDeleteCollectionDtl(criteria).then(data => {
            if (data && data.success) {
                collectionVM.searchDtl(hdrIds).then(data => {
                    setIsLoading(false);
                    messageBarVM.showSuccess("Delete Successfully.");
                });
                setIsLoading(false);
            } else {
                messageBarVM.showError(data.data ?? "Delete Failed.");
                setIsLoading(false);
                return;
            }
        });
    }, [collectionHdrList, collectionVM, messageBarVM, selectedDtlRows, selectedRows])

    const handClose = useCallback(async () => {
        if ((operatingUnit === 'SAPT' || operatingUnit === 'KICT')) {
            const amt = collectionDtlList?.reduce((sum, item) => sum + (item.paymentAmount ?? 0), 0);
            if (collectionDtlList.length > 0 && totalInvAmt > amt) {
                setIsShowClose(true);
            } else {
                setIsLoading(true);
                if (collectionDtlList.length === 0) {
                    let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
                    criteria = {
                        ...criteria,
                        selectRows: selectedRows,
                        delDtlIds: [],
                        allRows: collectionHdrList,
                    }
                    await collectionVM.onleaveDtl(criteria).then(async data => {
                        collectionVM.onCloseClick();
                        await collectionVM.onSearch(searchCriteria).then(data => {
                            setIsLoading(false);
                        });
                        setIsLoading(false);
                    });
                    return;
                }
                collectionVM.onCloseClick();
                await collectionVM.onSearch(searchCriteria).then(data => {
                    setIsLoading(false);
                });
            }
        } else {
            collectionVM.onCloseClick();
            await collectionVM.onSearch(searchCriteria).then(data => {
                setIsLoading(false);
            });
        }
    }, [collectionDtlList, collectionHdrList, collectionVM, searchCriteria, selectedRows, totalInvAmt, operatingUnit])

    const handleClose = useCallback(() => {
        setIsShowClose(false);
        setIsLoading(true);
        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        const ids = collectionDtlList.map(ety => ety.id);
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            delDtlIds: ids,
            allRows: collectionHdrList
        }
        collectionVM.onleaveDtl(criteria).then(async data => {
            collectionVM.onCloseClick();
            await collectionVM.onSearch(searchCriteria).then(data => {
                setIsLoading(false);
            });
            setIsLoading(false);
        });
    }, [collectionHdrList, collectionVM, searchCriteria, collectionDtlList, selectedRows])

    const handleCancel = useCallback(() => {
        setIsShowClose(false);
    }, [])

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        collectionVM.updateSelectedDtlRows(selectedRows);
    }, [collectionVM])

    const isPaymentModeLpaOrEdi = useCallback(() => {
        if (collectionDtlList.some(entity => entity.paymentMode === 'LPA' || entity.paymentMode === 'BANK EDI')
            || selectedRows.some(entity => entity.receiptNo !== null && entity.receiptNo !== '' && entity.receiptNo !== undefined)) {
            return false;
        }
        return true;
    }, [collectionDtlList, selectedRows])

    const handleApply = useCallback(async () => {
        setIsLoading(true);
        let criteria = { ...EMPTY_COLLECTION_DTL_SEARCH_CRITERIA };
        criteria = {
            ...criteria,
            selectRows: selectedRows,
            allDtls: collectionDtlList,
        }
        collectionVM.onSaveCollectionDtl(criteria).then(data => {
            if (data && data.success) {
                const hdrIds = selectedRows.map(ety => ety.id);
                collectionVM.searchDtl(hdrIds).then(data => {
                    setIsLoading(false);
                    messageBarVM.showSuccess("Save Successfully.");
                });
                setIsLoading(false);
            } else {
                messageBarVM.showError(data.data ?? "Save Failed.");
                setIsLoading(false);
                return;
            }
        });
    }, [collectionDtlList, collectionVM, messageBarVM, selectedRows]);

    const handleTableDataChanged = useCallback((newData: CollectionDetailEntity[]) => {
        newData.forEach(item => {

            if (item.payee) {
                item.payeeName = !_.isEmpty(payeeNameListRef.current) ? payeeNameListRef.current.find(company => company.companyCode === item.payee)?.companyName : ''
            }
        });
        collectionVM.onEditTempPaymentData(newData);
    }, [collectionVM]);

    const memoTableTitle = useMemo(() => {
        return (
            <>
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                    {((operatingUnit === 'SAPT' || operatingUnit === 'KICT') && !isShowEditPanel && isPaymentModeLpaOrEdi()) &&
                        <div style={{ marginRight: "5px" }}>
                            <HPHButton disabled={false} label={'Apply'} size={'Small'} theme={'Secondary'} onClick={handleApply} />
                        </div>
                    }
                </div>
            </>
        )
    }, [handleApply, isPaymentModeLpaOrEdi, isShowEditPanel, operatingUnit])


    const memoBtns = useMemo(() => {

        const hdrBtns = [];
        if ((operatingUnit === 'SAPT' || operatingUnit === 'KICT') && !isShowEditPanel && isPaymentModeLpaOrEdi()) {
            hdrBtns.push({
                id: 'onDeleteButton',
                icon: 'Icon-trash',
                title: 'Delete'
            })
        }

        return hdrBtns
    }, [isPaymentModeLpaOrEdi, isShowEditPanel, operatingUnit])

    const isRowSelectable = useCallback((params: any) => {
        return !isShowEditPanel;
    }, [isShowEditPanel]);


    const memoTable = useMemo(() => {
        return (
            <NbisTable2
                id='collection-dtl-table'
                isNewColumnSetting={true}
                columns={(operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? INITIAL_COLLECTION_DTL_COL_DEF(collectionState.dynamicOptions, isPaymentModeLpaOrEdi()) : INITIAL_COLLECTION_DTL_COL_DEF_OTHER}
                data={transferRowData(collectionDtlList ?? [])}
                showPaginator={false}
                editable={true}
                showAddIcon={(operatingUnit === 'SAPT' || operatingUnit === 'KICT') && !isShowEditPanel && isPaymentModeLpaOrEdi()}
                onAddClick={handleAdd}
                onDeleteButton={handleDelete}
                showDeleteButton={false}
                showReloadIcon={false}
                showUploadIcon={false}
                isScrollHighlighted={true}
                selectionMode={true}
                rowSelection={"multiple"}
                isRowHighligted={true}
                handleSelectionChanged={handleSelectionChange}
                gridHeight="customHeight"
                customHeight="calc(100vh - 250px)"
                ref={gridRef}
                headerActionButtons={memoBtns}
                headerLabel={memoTableTitle}
                handleRowSelectable={isRowSelectable}
                onTableDataChanged={handleTableDataChanged}
                multiColumnsEdit={true}
            />
        );
    }, [collectionDtlList, collectionState.dynamicOptions, handleAdd, handleDelete, handleSelectionChange, handleTableDataChanged, isPaymentModeLpaOrEdi, isRowSelectable, isShowEditPanel, memoBtns, memoTableTitle, operatingUnit])

    useEffect(() => {
        if (gridRef.current && gridRef.current.gridRef && gridRef.current.gridRef.current && gridRef.current.gridRef.current.api) {
            gridRef.current.gridRef.current.api.redrawRows();
        }
        payeeNameListRef.current = payeeNameList;
    }, [isShowEditPanel, payeeNameList, collectionDtlList])

    return <>
        <div className="main-comp-wrapper">
            <div className={'flex-row'}>
                <div className={'flex-row-item flex-row-item-full-width'}>
                    <Sidebarheader>
                        <SidebarTitle></SidebarTitle>
                        <SidebarActionCross>
                            {(!isShowEditPanel) && <IconButton
                                fileName="Icon-cross" size="medium" toolTipText='Close' toolTipArrow={false} onClick={handClose} />}
                        </SidebarActionCross>
                    </Sidebarheader>
                </div>
            </div>
            <CollectionHeaderBar />
            <TableWrapper>
                {isLoading &&
                    <Loader Indicator="Spinner" size="Medium" />}
                {memoTable}
                <ConfirmModal headerText={`${WorkspaceConstant.Common.BUTTON_CLOSE} ${SCREEN_CONSTANT.TITLE}`}
                    contentText={`${"Insufficient fund for settling the invoices."}\n${"All payment records will be cancelled.Quit anyway?"}`}
                    visible={isShowClose && (operatingUnit === 'SAPT' || operatingUnit === 'KICT')}
                    onCancelClick={handleCancel}
                    onConfirmClick={handleClose}
                />
            </TableWrapper>
        </div>
    </>;
}

export default memo(CollectionDtlTablePanel);
